/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AsyncSelect from "react-select/async";

// Material UI table
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  MenuItem,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Close";

import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { commonHeaderLinkLists } from "helper/PatientManagementHelper/PatientManagementHelper";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import ConditionsHeader from "./Header/ConditionsHeader";
import {
  getDateFormatInSpanish,
  mergeListById,
} from "helper/Common/CommonHelper";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import { assetImages } from "constants";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import SaveConditionPopup from "./Popup/SaveConditionPopup";
import AssignUserModal from "components/Common/Modal/AssignUserModal";

const ConditionsListBody = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const [isLoading, setIsLoading] = useState(false);
  const [conditionList, setConditionList] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedConditionIds, setSelectedConditionIds] = useState([]);
  const [selectedConditionId, setSelectedConditionId] = useState(null);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // Inline editing state
  const [editingCell, setEditingCell] = useState({ rowId: null, column: null });
  const [editingValue, setEditingValue] = useState("");

  //function for get all conditions
  const getAllConditions = async () => {
    try {
      setIsLoading(true);

      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_MEDICAL_CONDITION +
        `?token=${token}`;

      console.log("requestUrl", requestUrl);

      const response = await getData(requestUrl);

      setIsLoading(false);

      console.log("response", response);

      if (response.status) {
        setConditionList(response.data);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  //function for bulk update condition
  const updateBulkConditionHandler = async () => {
    if (selectedConditionIds.length > 0) {
      try {
        const conditionListToBeUpdate = conditionList
          .filter((item) => selectedConditionIds.includes(item._id))
          .map((item) => {
            return {
              _id: item._id,
              condition: item.condition,
              conditionname: item.conditionname,
              reportedon: item.reportedon,
              resolvedon: item.resolvedon,
              currentstatus: item.currentstatus,
            };
          });

        let conditionData = {
          medicalconditionlist: conditionListToBeUpdate,
        };

        console.log(
          "conditionData in update bulk condition handler",
          conditionData
        );

        let requestURL =
          url.API_BASE_URL +
          url.API_UPDATE_BULK_MEDICAL_CONDITION +
          `?token=${token}`;

        const response = await putData(requestURL, conditionData);

        console.log("response in update bulk condition handler", response);

        if (response.status) {
          setMessageType("success");

          setTimeout(() => {
            setSelectedConditionIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }

        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one condition");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  //function for delete condition
  const deleteBulkConditionHandler = async () => {
    if (selectedConditionIds.length > 0) {
      try {
        const conditionListToBeDelete = conditionList.filter((item) =>
          selectedConditionIds.includes(item._id)
        );

        let conditionData = {
          medicalconditionlist: conditionListToBeDelete,
        };

        let requestURL =
          url.API_BASE_URL +
          url.API_DELETE_BULK_MEDICAL_CONDITION +
          `?token=${token}`;

        const response = await putData(requestURL, conditionData);

        console.log(response);

        if (response.status) {
          setMessageType("success");

          setTimeout(() => {
            setConditionList((prevList) =>
              prevList.filter(
                (item) => !selectedConditionIds.includes(item._id)
              )
            );

            setSelectedConditionIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }

        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one condition");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  //function for after any action
  const afterActionClose = (data = []) => {
    setConditionList((prevList) => mergeListById(prevList, data));
  };

  useEffect(() => {
    getAllConditions();
  }, []);

  // Common function to save cell edits for name, reported date, resolved date
  const handleSaveCellEdit = (rowId, column, value, extra = {}) => {
    setConditionList((prevList) =>
      prevList.map((item) => {
        if (item._id !== rowId) return item;
        if (column === "condition") {
          return {
            ...item,
            condition: value?.name ?? value?.label,
            conditionname: value?.name ?? value?.label,
          };
        }
        if (column === "reportedon") {
          return {
            ...item,
            reportedon: extra.isoDate || "",
            reportedondate: extra.formattedDate || "",
          };
        }
        if (column === "resolvedon") {
          return {
            ...item,
            resolvedon: extra.isoDate || "",
            resolvedondate: extra.formattedDate || "",
          };
        }
        if (column === "currentstatus") {
          // Always store as string
          return {
            ...item,
            currentstatus:
              typeof value === "string"
                ? value
                : value?.value || value?.label || "",
          };
        }
        // fallback for other columns
        return { ...item, [column]: value };
      })
    );
    setEditingCell({ rowId: null, column: null });
    setEditingValue("");
    setRowSelection((prevSelection) => ({
      ...prevSelection,
      [rowId]: true,
    }));
    setSelectedConditionIds((prevIds) =>
      prevIds.includes(rowId) ? prevIds : [...prevIds, rowId]
    );
  };

  // Add this function to fix the error
  const handleCancelEdit = () => {
    setEditingCell({ rowId: null, column: null });
    setEditingValue("");
  };

  // Async load options for medical condition (same as SaveConditionPopup)
  const loadMedicalConditionOptions = async (inputValue) => {
    if (!inputValue || inputValue.length <= 1) {
      return [];
    }
    try {
      let requestUrl = url.SERVER_URL + url.MEDICAL_CONDITIONS_OPTIONS;
      const response = await getData(requestUrl);
      return response
        .filter((item) =>
          item.name?.toLowerCase().includes(inputValue.toLowerCase())
        )
        .map((item) => ({
          value: item.name ?? "", // Use name for backend, fallback to _id
          name: item.name ?? "",
          label: item.name ?? "",
          description: item.description ?? "",
          symptoms: item.symptoms ?? [],
          treatment: item.treatment ?? "",
        }));
    } catch (error) {
      return [];
    }
  };

  // Async load options for condition status
  const loadConditionStatusOptions = async (inputValue) => {
    try {
      let requestUrl = url.SERVER_URL + url.CONDITION_STATUS_OPTIONS;
      const response = await getData(requestUrl);
      // response is expected to be [{name, label, value}, ...]
      return response
        .filter((item) =>
          item.name?.toLowerCase().includes((inputValue || "").toLowerCase())
        )
        .map((item) => ({
          value: item.value ?? item.name ?? "",
          name: item.name ?? "",
          label: item.label ?? item.name ?? "",
        }));
    } catch (error) {
      return [];
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: t("Actions"),
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            data-bs-toggle="offcanvas"
            data-bs-target="#saveConditionOffCanvas"
            aria-controls="saveConditionOffCanvas"
            onClick={() => {
              setSelectedConditionId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </button>
        ),
        enableColumnActions: false, // Hides the column action icon
        enableColumnDragging: false, // Hides the move icon
        enableSorting: false,
      },
      {
        header: "Name",
        accessorKey: "condition",
        size: 200,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "condition" ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ flex: 1, minWidth: 220, maxWidth: 320 }}>
                <AsyncSelect
                  autoFocus
                  cacheOptions={false}
                  defaultOptions={false}
                  loadOptions={loadMedicalConditionOptions}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  value={
                    editingValue
                      ? editingValue
                      : row.original.condition
                      ? {
                          value: row.original.condition,
                          name: row.original.conditionname || "",
                          label: row.original.conditionname || "",
                        }
                      : null
                  }
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setEditingValue(selectedOption);
                    }
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "32px",
                      borderRadius: "8px",
                      borderColor: "#b2b2b2ff",
                      boxShadow: "none",
                      minWidth: 220,
                      maxWidth: 320,
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 220,
                      maxWidth: 320,
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 220,
                      maxWidth: 320,
                    }),
                  }}
                  placeholder="Search for Condition"
                />
              </Box>
              <IconButton
                size="small"
                onClick={() =>
                  handleSaveCellEdit(
                    row.original._id,
                    "condition",
                    editingValue // Pass the AsyncSelect value directly
                  )
                }
                sx={{
                  border: 1,
                  borderColor: "grey.main",
                  color: "grey.main",
                  bgcolor: "transparent",
                  ml: 1,
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  "&:hover": {
                    bgcolor: "grey.100",
                    borderColor: "grey.dark",
                    color: "grey.dark",
                  },
                }}
                disabled={!editingValue}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCancelEdit}
                sx={{
                  border: 1,
                  borderColor: "error.light",
                  color: "error.light",
                  bgcolor: "transparent",
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  ml: 0.5,
                  "&:hover": {
                    bgcolor: "error.lighter",
                    borderColor: "error.dark",
                    color: "error.dark",
                  },
                }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "condition",
                });
                setEditingValue(
                  row.original.condition
                    ? {
                        value: row.original.condition,
                        name: row.original.conditionname || "",
                        label: row.original.conditionname || "",
                      }
                    : null
                );
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.original.conditionname &&
              row.original.conditionname !== "" ? (
                row.original.conditionname
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Reported Date",
        accessorKey: "reportedondate",
        size: 160,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "reportedon" ? (
            <TextField
              size="small"
              type="date"
              value={
                editingValue ||
                (row.original.reportedon
                  ? new Date(row.original.reportedon).toISOString().slice(0, 10)
                  : "")
              }
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const isoDate = editingValue
                          ? new Date(editingValue).toISOString()
                          : "";
                        const formattedDate = editingValue
                          ? getDateFormatInSpanish(editingValue)
                          : "";
                        handleSaveCellEdit(
                          row.original._id,
                          "reportedon",
                          editingValue, // Pass the date string value
                          { isoDate, formattedDate }
                        );
                      }}
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const isoDate = editingValue
                    ? new Date(editingValue).toISOString()
                    : "";
                  const formattedDate = editingValue
                    ? getDateFormatInSpanish(editingValue)
                    : "";
                  handleSaveCellEdit(
                    row.original._id,
                    "reportedon",
                    editingValue, // Pass the date string value
                    { isoDate, formattedDate }
                  );
                }
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <div
              className="joined border-bottom-0"
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "reportedon",
                });
                setEditingValue(
                  row.original.reportedon
                    ? new Date(row.original.reportedon)
                        .toISOString()
                        .slice(0, 10)
                    : ""
                );
              }}
              style={{ cursor: "pointer" }}
            >
              <p className="d-flex align-items-center gap-2">
                <span className="d-block material-symbols-outlined icon-md">
                  calendar_month
                </span>
                <span className="d-block">{row.original.reportedondate}</span>
              </p>
            </div>
          ),
      },
      {
        header: "Resolved Date",
        accessorKey: "resolvedondate",
        size: 160,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "resolvedon" ? (
            <TextField
              size="small"
              type="date"
              value={
                editingValue ||
                (row.original.resolvedon
                  ? new Date(row.original.resolvedon).toISOString().slice(0, 10)
                  : "")
              }
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const isoDate = editingValue
                          ? new Date(editingValue).toISOString()
                          : "";
                        const formattedDate = editingValue
                          ? getDateFormatInSpanish(editingValue)
                          : "";
                        handleSaveCellEdit(
                          row.original._id,
                          "resolvedon",
                          editingValue, // Pass the date string value
                          { isoDate, formattedDate }
                        );
                      }}
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const isoDate = editingValue
                    ? new Date(editingValue).toISOString()
                    : "";
                  const formattedDate = editingValue
                    ? getDateFormatInSpanish(editingValue)
                    : "";
                  handleSaveCellEdit(
                    row.original._id,
                    "resolvedon",
                    editingValue, // Pass the date string value
                    { isoDate, formattedDate }
                  );
                }
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <div
              className="joined border-bottom-0"
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "resolvedon",
                });
                setEditingValue(
                  row.original.resolvedon
                    ? new Date(row.original.resolvedon)
                        .toISOString()
                        .slice(0, 10)
                    : ""
                );
              }}
              style={{ cursor: "pointer" }}
            >
              <p className="d-flex align-items-center gap-2">
                <span className="d-block material-symbols-outlined icon-md">
                  calendar_month
                </span>
                <span className="d-block">{row.original.resolvedondate}</span>
              </p>
            </div>
          ),
      },
      {
        header: "Current Status",
        accessorKey: "currentstatus",
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "currentstatus" ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ flex: 1, minWidth: 180, maxWidth: 220 }}>
                <AsyncSelect
                  autoFocus
                  cacheOptions={true}
                  defaultOptions
                  loadOptions={loadConditionStatusOptions}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  value={
                    editingValue
                      ? editingValue
                      : row.original.currentstatus
                      ? {
                          value: row.original.currentstatus,
                          name: row.original.currentstatus,
                          label: row.original.currentstatus,
                        }
                      : null
                  }
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setEditingValue(selectedOption);
                    }
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "32px",
                      borderRadius: "8px",
                      borderColor: "#b2b2b2ff",
                      boxShadow: "none",
                      minWidth: 180,
                      maxWidth: 220,
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 180,
                      maxWidth: 220,
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                      minWidth: 180,
                      maxWidth: 220,
                    }),
                  }}
                  placeholder="Select Status"
                />
              </Box>
              <IconButton
                size="small"
                onClick={() =>
                  handleSaveCellEdit(
                    row.original._id,
                    "currentstatus",
                    editingValue?.value || editingValue?.label || ""
                  )
                }
                sx={{
                  border: 1,
                  borderColor: "grey.main",
                  color: "grey.main",
                  bgcolor: "transparent",
                  ml: 1,
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  "&:hover": {
                    bgcolor: "grey.100",
                    borderColor: "grey.dark",
                    color: "grey.dark",
                  },
                }}
                disabled={!editingValue}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCancelEdit}
                sx={{
                  border: 1,
                  borderColor: "error.light",
                  color: "error.light",
                  bgcolor: "transparent",
                  borderRadius: "8px",
                  minWidth: 28,
                  height: 28,
                  ml: 0.5,
                  "&:hover": {
                    bgcolor: "error.lighter",
                    borderColor: "error.dark",
                    color: "error.dark",
                  },
                }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "currentstatus",
                });
                setEditingValue(
                  row.original.currentstatus
                    ? {
                        value: row.original.currentstatus,
                        name: row.original.currentstatus,
                        label: row.original.currentstatus,
                      }
                    : null
                );
              }}
              style={{
                cursor: "pointer",
                minHeight: "24px",
                display: "inline-block",
                width: "100%",
              }}
            >
              {/* Always show the string value */}
              {typeof row.original.currentstatus === "string" &&
              row.original.currentstatus !== "" ? (
                row.original.currentstatus
              ) : (
                <span style={{ color: "#bbb" }}></span>
              )}
            </span>
          ),
      },
      {
        header: "Assigned To",
        accessorKey: "assignedpersonname",
        Cell: ({ row }) => (
          <div
            className={
              row.original.assignedpersonname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.assignedpersonimage == ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.assignedpersonimage
              }
              alt="Babcock"
            />
            <p>
              <span className="d-block">{row.original.assignedpersonname}</span>
            </p>
            {/* <button
                      onClick={() => {
                        createTaskChatHandler(row.original._id);
                      }}
                      className={
                        row.original.moderator.toString() === userInfo._id.toString()
                          ? "d-none"
                          : "d-flex align-items-center ms-auto border-0 bg-light"
                      }
                    >
                      <span className="d-block material-symbols-outlined icon-md text-primary">
                        chat
                      </span>
                    </button> */}
          </div>
        ),
      },
    ],
    [editingCell, editingValue]
  );

  //initialize the column order
  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ]; //array of column ids (Initializing is optional as of v2.10.0)

  useEffect(() => {
    const selectedIdsArray = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );

    setSelectedConditionIds(selectedIdsArray);
  }, [rowSelection]);

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN" ||
    moduleAccess.includes("MOD_APPOINTMENT_MANAGEMENT")
  ) {
    return (
      <div id="content_wrapper">
        <section className="crm-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderLinkLists(t)}
            activeOption={t("Conditions")}
          />
          <div className="container-fluid px-lg-5">
            <ConditionsHeader
              reloadList={getAllConditions}
              selectedItemIds={selectedConditionIds}
              updateBulkHandler={updateBulkConditionHandler}
            />

            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns}
                  data={conditionList}
                  enableGrouping // to enable grouping of column
                  enableRowSelection // enable showing checkbox in each row
                  getRowId={(row) => row._id}
                  onRowSelectionChange={setRowSelection}
                  state={{ rowSelection, columnOrder }}
                  initialState={{ rowSelection, columnOrder }} // to set the initial state of column order
                  defaultColumn={{
                    minSize: 20,
                    maxSize: 300,
                    size: 50, //make columns wider by default
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "60vh",
                    },
                  }}
                  enableStickyHeader
                />
              </div>
            )}
          </div>

          {/* popup and modals */}

          <SaveConditionPopup
            selectedConditionId={selectedConditionId}
            setSelectedConditionId={setSelectedConditionId}
            afterActionClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
          />

          <AssignUserModal
            moduleName="condition"
            afterModalClose={afterActionClose}
            selectedIds={selectedConditionIds}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
            onModalClose={() => {
              setRowSelection({});
              setSelectedConditionIds([]);
            }}
          />

          <DeleteWarningModal
            onDelete={deleteBulkConditionHandler}
            onCancelDelete={() => {
              setRowSelection({});
              setSelectedConditionIds([]);
            }}
            moduleName={t("condition")}
          />

          {showAlert && (
            <AlertNotification
              showAlert={showAlert}
              message={alertMessage}
              alertType={messageType}
              onClose={onAlertClose}
            />
          )}
        </section>
      </div>
    );
  } else {
    return <AccessDeniedView />;
  }
};

export default ConditionsListBody;
